/* eslint-disable sonarjs/no-duplicate-string */
import { FC, useContext } from "react";

import { FormCategory, FormType, isGiftAidEligible } from "@simplyk/common";
import { UseFormReturn } from "react-hook-form";

import { FrontendFormContext } from "../../../contexts/FrontendFormContext";
import { TicketingPaymentInput } from "../../../types/ticketing";
import { DonationPaymentInput, useFormV2Context } from "../context/FormV2Context";
import { HeartsAnimationProvider } from "../context/heartsAnimationContext";
import { GiftAidBlock } from "../sharedComponents/GiftAidBlock";

import { DonationFormV2CheckoutFooter } from "./CheckoutFooter/DonationFormV2CheckoutFooter";
import { DonationFormV2Step1Layout } from "./DonationFormV2Step1Layout";

import { NotificationBannerHeightContext } from "@/components/NotificationBanners/NotificationBannerHeightContext";
import { useThemedFormZeffyLogoUrl } from "@/features/FormV2/hooks/useThemedFormZeffyLogoUrl";
import { useUtmForm } from "@/features/FormV2/hooks/useUtmForm";
import { ThemedDonor } from "@/features/FormV2/sharedComponents/ThemedDonor";
import { ThemedDonorsListBlock } from "@/features/FormV2/sharedComponents/ThemedDonorsListBlock";
import { ThemedDonorsListDialog } from "@/features/FormV2/sharedComponents/ThemedDonorsListDialog";
import {
  useThemedDonorListDialogState,
  useThemedDonorListDialogServerState,
} from "@/features/FormV2/sharedComponents/useThemedDonorListDialogState";
import { useThemedDonorsListBlockState } from "@/features/FormV2/sharedComponents/useThemedDonorsListBlockState";
import { Amounts } from "@/features/FormV2/Step1/DonationForm/Amounts";
import { DonationFormDivider } from "@/features/FormV2/Step1/DonationForm/DonationFormDivider";
import { useDonationFirstStepSubmit } from "@/features/FormV2/Step1/DonationForm/useDonationFirststepSubmit";
import { FormV2GenerateReceiptInfobox } from "@/features/FormV2/Step1/FormV2GenerateReceiptInfobox";
import { FormV2LogoChip } from "@/features/FormV2/Step1/FormV2LogoChip";
import { FormV2ZeffyBusinessModelInfobox } from "@/features/FormV2/Step1/FormV2ZeffyBusinessModelInfobox";
import { MobileFormV2Banner } from "@/features/FormV2/Step1/MobileFormV2Banner";
import { DonationFormV2Summary } from "@/features/FormV2/Step1/Summary/DonationFormV2Summary";
import { useTrackThemedFormLogoClicked } from "@/features/FormV2/tracking/useTrackThemedFormLogoClicked";
import { usePreviewContext } from "@/features/LiveFormEditor/LivePreview/context/PreviewContext";

export type DonationFormV2Step1Props = {
  isEmbed?: boolean;
};

export const DonationFormV2Step1: FC<DonationFormV2Step1Props> = ({ isEmbed = false }) => {
  const { formObject, hasBanner, description, form } = useFormV2Context(FormType.DonationForm);
  const { displayedFormAmount } = useContext(FrontendFormContext);

  const { isExpanded, bannerHeight } = useContext(NotificationBannerHeightContext);
  const { isPreview } = usePreviewContext();
  const { canSubmitFirstStep } = useDonationFirstStepSubmit();
  const utm = useUtmForm();
  const organizationIsGiftAidEligible = isGiftAidEligible(formObject.organization);
  const donorCanOptInGiftAid = Boolean(organizationIsGiftAidEligible && displayedFormAmount);

  const onTrackLogoClick = useTrackThemedFormLogoClicked({
    formType: FormType.DonationForm,
    formCategory: FormCategory.DonationForm,
    country: formObject.organization?.country,
    formStep: 1,
  });

  const logoHref = useThemedFormZeffyLogoUrl(utm);

  const isDonationFormWithTarget = formObject.target !== null;

  const { hasDonations, hasMoreDonations, latestDonations } = useThemedDonorsListBlockState({
    donationFormId: formObject.id,
    enabled: isDonationFormWithTarget,
  });

  const {
    open: donorsListDialogOpen,
    onOpen: onDonorsListDialogOpen,
    onClose: onDonorsListDialogClose,
  } = useThemedDonorListDialogState();

  const donorsDialogServerState = useThemedDonorListDialogServerState({
    donationFormId: formObject.id,
    open: donorsListDialogOpen,
  });

  const isOneColumnLayout = !hasBanner && !description;

  return (
    <HeartsAnimationProvider>
      <DonationFormV2Step1Layout
        isOneColumn={isOneColumnLayout}
        isEmbed={isEmbed}
        contextDrawerHeight={isExpanded ? bannerHeight : 0}
        slots={{
          banner: hasBanner ? <MobileFormV2Banner /> : undefined,
          organizationLogo: (
            <FormV2LogoChip organizationName={formObject?.organization?.name} logoUrl={formObject?.logoUrl} />
          ),
          summary: <DonationFormV2Summary forceInlined={isOneColumnLayout || isEmbed} />,
          donors: hasDonations && (
            <ThemedDonorsListBlock showViewAllButton={hasMoreDonations} onViewAllClick={onDonorsListDialogOpen}>
              {latestDonations.map((donation, index) => (
                <ThemedDonor {...donation} key={index} />
              ))}
            </ThemedDonorsListBlock>
          ),
          donationAmounts: formObject.isArchived ? undefined : <Amounts />,
          generateReceipt: <FormV2GenerateReceiptInfobox />,
          zeffyBusinessModel: <FormV2ZeffyBusinessModelInfobox />,
          checkoutFooter: <DonationFormV2CheckoutFooter />,
          divider: <DonationFormDivider />,
          giftAid: donorCanOptInGiftAid && (
            <GiftAidBlock
              amount={displayedFormAmount}
              form={form as UseFormReturn<DonationPaymentInput | TicketingPaymentInput>}
              organizationName={formObject.organization?.name || ""}
            />
          ),
        }}
        showCheckoutFooter={canSubmitFirstStep && !isPreview}
        logoHref={logoHref}
        onLogoClick={onTrackLogoClick}
      />
      {hasDonations && (
        <ThemedDonorsListDialog
          {...donorsDialogServerState}
          open={donorsListDialogOpen}
          onClose={onDonorsListDialogClose}
        />
      )}
    </HeartsAnimationProvider>
  );
};
